import flatten from 'lodash/flatten'
import moment, {Moment} from 'moment-timezone'
import {exhaustive} from '@freckle/exhaustive'
import {maybe, mthen} from '@freckle/maybe'
import {mmap} from '@freckle/maybe'
import {
  CompletionStatusT,
  isComplete as isCompleteHelper,
} from '@freckle/educator-entities/ts/common/helpers/completion-status'
import {fromParams} from '@freckle/educator-entities/ts/common/helpers/generic-assignment-sessions-params'
import {ParamsT} from '@freckle/educator-entities/ts/common/helpers/generic-assignment-sessions-params'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import {
  MathAdaptiveT,
  MathTargetedT,
  MathFocusSkillsPracticeT,
  MathAssessmentsT,
  MathFactPracticeT,
  MathIblsT,
  MathNumberFactsT,
  MathNumberBasicsT,
  MathDepthOfKnowledgePracticeT,
  MathTargetedDepthOfKnowledgePracticeT,
  ElaDecodablesT,
  ElaTargetedSkillsPracticeT,
  ElaFocusSkillsPracticeT,
  ElaAdaptiveSkillsPracticeT,
  ElaWordStudyT,
  ElaSightWordsT,
  ElaArticlesReadingT,
  ElaArticlesWritingT,
  ElaAssessmentsT,
  ScienceArticlesReadingT,
  ScienceArticlesWritingT,
  SocialStudiesArticlesReadingT,
  SocialStudiesArticlesWritingT,
  ProductT,
} from '@freckle/educator-entities/ts/common/helpers/products'
import {IsSelfAssignedT} from '@freckle/educator-entities/ts/common/helpers/self-assigned'
import {SubjectT} from '@freckle/educator-entities/ts/common/helpers/subjects'
import {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import {
  ArticleMetadataT,
  fromOpenApiArticlesWritingMetadata,
  fromOpenApiArticlesReadingMetadata,
  ResumableArticleMetadataT,
} from '@freckle/educator-entities/ts/common/models/generic-assignment-session/articles'
import {
  ElaAssessmentMetadataT,
  ElaDecodablesMetadataT,
  ElaAdaptiveSkillsPracticeMetadataT,
  ElaTargetedSkillsPracticeMetadataT,
  ElaFocusSkillsPracticeMetadataT,
  ElaWordStudyMetadataT,
  ElaSightWordsMetadataT,
  fromOpenApiElaTargetedSkillsPracticeMetadata,
  fromOpenApiElaWordStudyMetadata,
  fromOpenApiElaAdaptiveSkillsPracticeMetadata,
  fromOpenApiElaFocusSkillsPracticeMetadata,
} from '@freckle/educator-entities/ts/common/models/generic-assignment-session/ela'
import {
  HTMLText,
  PlainText,
  toPlainText,
} from '@freckle/educator-entities/ts/common/models/html-text'
import {ElaArticleInfoAttrs} from '@freckle/educator-entities/ts/ela/articles/models/ela-article-info'
import {fetchElaArticleInfoCollection} from '@freckle/educator-entities/ts/ela/articles/models/ela-article-info'
import {fetchElaAssessments} from '@freckle/educator-entities/ts/ela/assessments/models/ela-assessment'
import {getElaAssessmentLetter} from '@freckle/educator-entities/ts/ela/assessments/models/ela-assessment'
import {
  getAdaptiveSkillsTitle,
  getDecodableTitle,
  getTargetedSkillsTitle,
  getFocusSkillsTitle,
  getArticleTitle,
  getWordStudyTitle,
  getAssessmentTitle,
} from '@freckle/educator-entities/ts/ela/common/logic/assignments'
import {fetchDecodableInfos} from '@freckle/educator-entities/ts/ela/decodables/models/decodable-info'
import {
  operations,
  Ok,
  client,
  handleDataOrError,
} from '@freckle/educator-entities/ts/fancy-api-client'
import {
  MathAdaptiveMetadataT,
  MathTargetedMetadataT,
  MathFocusSkillsPracticeMetadataT,
  MathAssessmentMetadataT,
  MathIblMetadataT,
  MathFactPracticeMetadataT,
  MathNumberBasicsMetadataT,
  MathDepthOfKnowledgeMetadataT,
  MathTargetedDepthOfKnowledgeMetadataT,
  fromOpenApiMathFactPracticeMetadata,
  fromOpenApiMathAssessmentMetadata,
  fromOpenApiMathTargetedMetadata,
  fromOpenApiMathAdaptiveMetadata,
  fromOpenApiMathIblMetadata,
  fromOpenApiMathTargetedDepthOfKnowledgePracticeMetadata,
  fromOpenApiFocusSkillsPracticeMetadata,
} from './math'

export type GenericAssignmentSessionMetadataT =
  | {
      tag: MathAdaptiveT
      contents: MathAdaptiveMetadataT
    }
  | {
      tag: MathTargetedT
      contents: MathTargetedMetadataT
    }
  | {
      tag: MathFocusSkillsPracticeT
      contents: MathFocusSkillsPracticeMetadataT
    }
  | {
      tag: MathAssessmentsT
      contents: MathAssessmentMetadataT
    }
  | {
      tag: MathFactPracticeT
      contents: MathFactPracticeMetadataT
    }
  | {
      tag: MathIblsT
      contents: MathIblMetadataT
    }
  | {
      tag: MathNumberFactsT
    }
  | {
      tag: MathNumberBasicsT
      contents: MathNumberBasicsMetadataT
    }
  | {
      tag: MathDepthOfKnowledgePracticeT
      contents: MathDepthOfKnowledgeMetadataT
    }
  | {
      tag: MathTargetedDepthOfKnowledgePracticeT
      contents: MathTargetedDepthOfKnowledgeMetadataT
    }
  | {
      tag: ElaDecodablesT
      contents: ElaDecodablesMetadataT
    }
  | {
      tag: ElaTargetedSkillsPracticeT
      contents: ElaTargetedSkillsPracticeMetadataT
    }
  | {
      tag: ElaFocusSkillsPracticeT
      contents: ElaFocusSkillsPracticeMetadataT
    }
  | {
      tag: ElaAdaptiveSkillsPracticeT
      contents: ElaAdaptiveSkillsPracticeMetadataT
    }
  | {
      tag: ElaWordStudyT
      contents: ElaWordStudyMetadataT
    }
  | {
      tag: ElaSightWordsT
      contents: ElaSightWordsMetadataT
    }
  | {
      tag: ElaArticlesReadingT
      contents: ResumableArticleMetadataT
    }
  | {
      tag: ElaArticlesWritingT
      contents: ArticleMetadataT
    }
  | {
      tag: ElaAssessmentsT
      contents: ElaAssessmentMetadataT
    }
  | {
      tag: ScienceArticlesReadingT
      contents: ResumableArticleMetadataT
    }
  | {
      tag: ScienceArticlesWritingT
      contents: ArticleMetadataT
    }
  | {
      tag: SocialStudiesArticlesReadingT
      contents: ResumableArticleMetadataT
    }
  | {
      tag: SocialStudiesArticlesWritingT
      contents: ArticleMetadataT
    }

export type OpenApiSessionMetadata = OpenApiSession['metadata']

function fromOpenApiMetadata(x: OpenApiSessionMetadata): GenericAssignmentSessionMetadataT {
  switch (x.tag) {
    case 'social_studies_articles_reading':
      return {
        tag: Products.SocialStudiesArticlesReading,
        contents: fromOpenApiArticlesReadingMetadata(x.contents),
      }
    case 'science_articles_reading':
      return {
        tag: Products.ScienceArticlesReading,
        contents: fromOpenApiArticlesReadingMetadata(x.contents),
      }
    case 'ela_articles_reading':
      return {
        tag: Products.ElaArticlesReading,
        contents: fromOpenApiArticlesReadingMetadata(x.contents),
      }
    case 'social_studies_articles_writing':
      return {
        tag: Products.SocialStudiesArticlesWriting,
        contents: fromOpenApiArticlesWritingMetadata(x.contents),
      }
    case 'science_articles_writing':
      return {
        tag: Products.ScienceArticlesWriting,
        contents: fromOpenApiArticlesWritingMetadata(x.contents),
      }
    case 'ela_articles_writing':
      return {
        tag: Products.ElaArticlesWriting,
        contents: fromOpenApiArticlesWritingMetadata(x.contents),
      }
    case 'math_ibls':
      return {tag: Products.MathIbls, contents: fromOpenApiMathIblMetadata(x.contents)}
    case 'math_depth_of_knowledge_practice':
      return {
        tag: Products.MathDepthOfKnowledgePractice,
        contents: x.contents,
      }
    case 'ela_targeted_skills_practice':
      return fromOpenApiElaTargetedOrFsp(x)
    case 'ela_word_study':
      return {
        tag: Products.ElaWordStudy,
        contents: fromOpenApiElaWordStudyMetadata(x.contents),
      }
    case 'math_number_basics':
      return {
        tag: Products.MathNumberBasics,
        contents: x.contents,
      }
    case 'ela_adaptive_skills_practice':
      return {
        tag: Products.ElaAdaptiveSkillsPractice,
        contents: fromOpenApiElaAdaptiveSkillsPracticeMetadata(x.contents),
      }
    case 'ela_assessments':
      return {
        tag: Products.ElaAssessments,
        contents: {assessmentContentUuid: x.contents.assessmentContentUuid},
      }
    case 'ela_decodables':
      return {
        tag: Products.ElaDecodables,
        contents: {decodableUuid: x.contents.decodableUuid},
      }
    case 'math_number_facts':
      return {
        tag: Products.MathNumberFacts,
      }
    case 'math_fact_practice':
      return {
        tag: Products.MathFactPractice,
        contents: fromOpenApiMathFactPracticeMetadata(x.contents),
      }
    case 'math_assessments':
      return {
        tag: Products.MathAssessments,
        contents: fromOpenApiMathAssessmentMetadata(x.contents),
      }
    case 'math_targeted':
      return fromOpenApiMathTargetedOrFsp(x)
    case 'math_targeted_depth_of_knowledge_practice':
      return {
        tag: Products.MathTargetedDepthOfKnowledgePractice,
        contents: fromOpenApiMathTargetedDepthOfKnowledgePracticeMetadata(x.contents),
      }
    case 'ela_sight_words':
      return {
        tag: Products.ElaSightWords,
        contents: x.contents,
      }
    case 'math_adaptive':
      return {
        tag: Products.MathAdaptive,
        contents: fromOpenApiMathAdaptiveMetadata(x.contents),
      }
    default:
      exhaustive(x)
  }
}

function fromOpenApiMathTargetedOrFsp(
  x: Extract<OpenApiSessionMetadata, {tag: 'math_targeted'}>
): GenericAssignmentSessionMetadataT {
  const {standard} = x.contents
  if (x.contents.assignmentIsAligned && standard !== null && standard !== undefined) {
    return {
      tag: Products.MathTargeted,
      contents: fromOpenApiMathTargetedMetadata({...x.contents, standard}),
    }
  } else {
    return {
      tag: Products.MathFocusSkillsPractice,
      contents: fromOpenApiFocusSkillsPracticeMetadata(x.contents),
    }
  }
}

function fromOpenApiElaTargetedOrFsp(
  x: Extract<OpenApiSessionMetadata, {tag: 'ela_targeted_skills_practice'}>
): GenericAssignmentSessionMetadataT {
  if (x.contents.assignmentIsAligned) {
    return {
      tag: Products.ElaTargetedSkillsPractice,
      contents: fromOpenApiElaTargetedSkillsPracticeMetadata(x.contents),
    }
  } else {
    return {
      tag: Products.ElaFocusSkillsPractice,
      contents: fromOpenApiElaFocusSkillsPracticeMetadata(x.contents),
    }
  }
}

export type GenericAssignmentSessionAttrsT = {
  sessionId: number
  assignmentId: number
  isSelfAssigned: IsSelfAssignedT
  studentId: number
  teacherId: number | undefined | null
  createdAt: Moment
  completedAt: Moment | undefined | null
  startsAt: Moment
  durationSeconds: number | undefined | null
  numQuestionsAnswered: number | undefined | null
  accuracy: number | undefined | null
  status: CompletionStatusT
  subject: SubjectT
  metadata: GenericAssignmentSessionMetadataT
  title: HTMLText | PlainText | undefined | null
}

export const getPracticeMode = (t: TranslateT, session: GenericAssignmentSessionAttrsT): string =>
  Products.getPracticeMode(t, session.metadata.tag)

export type OpenApiSession = Ok<operations['getSubjectProductSessionR']>

export function fromOpenApiSession(x: OpenApiSession): GenericAssignmentSessionAttrsT {
  const {teacherId, durationSeconds, numQuestionsAnswered, accuracy} = x
  return {
    ...x,
    teacherId,
    durationSeconds,
    numQuestionsAnswered,
    accuracy,
    sessionId: x.id,
    createdAt: moment(x.createdAt),
    completedAt: mmap(moment, x.completedAt),
    startsAt: moment(x.startsAt),
    metadata: fromOpenApiMetadata(x.metadata),
    title: mmap(toPlainText, x.title),
  }
}

export const isSelfAssigned = (session: GenericAssignmentSessionAttrsT): boolean =>
  session.isSelfAssigned === 'self_assigned'

export function isTargetedOrTeacherAssigned(session: GenericAssignmentSessionAttrsT): boolean {
  const {metadata, isSelfAssigned} = session
  return metadata.tag === 'math_targeted' || isSelfAssigned !== 'self_assigned'
}

export function isComplete(session: GenericAssignmentSessionAttrsT): boolean {
  return isCompleteHelper(session.status)
}

export function needsGrading(session: GenericAssignmentSessionAttrsT): boolean {
  const {tag} = session.metadata
  if (
    tag === 'ela_articles_writing' ||
    tag === 'social_studies_articles_writing' ||
    tag === 'science_articles_writing'
  ) {
    const status = session.status
    const accuracy = session.accuracy
    return status === 'completed' && (accuracy === null || accuracy === undefined)
  }
  return false
}

// Fetch a single session for given product
export async function fetchGenericAssignmentSessionForProduct(
  product: ProductT,
  sessionId: number
): Promise<GenericAssignmentSessionAttrsT> {
  const paths = Products.getPaths(product)
  return client
    .GET(
      '/3/subjects/{SubjectP-Subject}/products/{ProductP-AbbreviatedProduct}/sessions/{SubjectProductSessionP-Int64}',
      {
        params: {
          path: {
            'SubjectP-Subject': paths.subjectPath,
            'ProductP-AbbreviatedProduct': paths.productPath,
            'SubjectProductSessionP-Int64': sessionId.toString(),
          },
        },
      }
    )
    .then(handleDataOrError)
    .then(fromOpenApiSession)
}

// Fetch sessions for a set of products in classroom
export async function fetchClassroomGenericAssignmentSessions(
  courseId: number,
  products: Array<ProductT>,
  params: ParamsT
): Promise<Array<GenericAssignmentSessionAttrsT>> {
  const resolvedSessions = await Promise.all(
    products.map(async product => {
      const paths = Products.getPaths(product)
      return client
        .GET(
          '/3/courses/{V3CourseP-CourseId}/subjects/{CourseSubjectP-Subject}/products/{CourseProductP-AbbreviatedProduct}/sessions',
          {
            params: {
              path: {
                'V3CourseP-CourseId': courseId,
                'CourseSubjectP-Subject': paths.subjectPath,
                'CourseProductP-AbbreviatedProduct': paths.productPath,
              },
              query: fromParams(params),
            },
          }
        )
        .then(handleDataOrError)
        .then(data => data.map(fromOpenApiSession))
    })
  )
  return flatten(resolvedSessions)
}

// Fetch sessions for a single product in all courses
export async function fetchClassroomGenericAssignmentSessionsForCourses(
  courses: Array<number>,
  product: ProductT,
  params: ParamsT
): Promise<Array<GenericAssignmentSessionAttrsT>> {
  const resolvedSessions = await Promise.all(
    courses.map(async courseId => {
      const paths = Products.getPaths(product)
      return client
        .GET(
          '/3/courses/{V3CourseP-CourseId}/subjects/{CourseSubjectP-Subject}/products/{CourseProductP-AbbreviatedProduct}/sessions',
          {
            params: {
              path: {
                'V3CourseP-CourseId': courseId,
                'CourseSubjectP-Subject': paths.subjectPath,
                'CourseProductP-AbbreviatedProduct': paths.productPath,
              },
              query: fromParams(params),
            },
          }
        )
        .then(handleDataOrError)
        .then(data => data.map(fromOpenApiSession))
    })
  )
  return flatten(resolvedSessions)
}

// Fetch sessions for a set of products in school
export async function fetchSchoolGenericAssignmentSessions(
  schoolId: number,
  products: Array<ProductT>,
  params: ParamsT
): Promise<Array<GenericAssignmentSessionAttrsT>> {
  const resolvedSessions = await Promise.all(
    products.map(async product => {
      const paths = Products.getPaths(product)
      return client
        .GET(
          '/3/schools/{V3SchoolP-SchoolId}/subjects/{SchoolSubjectP-Subject}/products/{SchoolProductP-AbbreviatedProduct}/sessions',
          {
            params: {
              path: {
                'V3SchoolP-SchoolId': schoolId,
                'SchoolSubjectP-Subject': paths.subjectPath,
                'SchoolProductP-AbbreviatedProduct': paths.productPath,
              },
              query: fromParams(params),
            },
          }
        )
        .then(handleDataOrError)
        .then(data => data.map(fromOpenApiSession))
    })
  )
  return flatten(resolvedSessions)
}

export const filterGenericAssignmentSessionsForProduct = (
  sessions: Array<GenericAssignmentSessionAttrsT>,
  product: ProductT
): Array<GenericAssignmentSessionAttrsT> =>
  sessions.filter(session => session.metadata.tag === product)

export const filterGenericAssignmentSessionsForProducts = (
  sessions: Array<GenericAssignmentSessionAttrsT>,
  products: ProductT[]
): Array<GenericAssignmentSessionAttrsT> =>
  sessions.filter(session => products.includes(session.metadata.tag))

// Get title for ELA products.
//
// This function will trivially return null for non-ELA products. We often
// displaying either Math products OR ELA products. Since we know that
// beforehand, there is no need to force clients to fetch the data needed to
// generate Math titles when there shouldn't be any math sessions.
//
// This function is also "best effort". For example, if a decodable session is
// associated with a decodable that is not in the collection, we simply return
// null. The client of the function can decide to error.
//
// The function is a port of the logic for `mkAssignmentsTableDataModel`.
export const getElaAssignmentTitles = async (
  t: TranslateT,
  sessions: Array<GenericAssignmentSessionAttrsT>,
  skills: {shortName: string; uspId: string}[]
): Promise<Array<GenericAssignmentSessionAttrsT>> =>
  Promise.all(
    sessions.map(async session => {
      const mTitle = await maybe(
        () => getAssignmentTitleForElaProduct(t, session, skills),
        title => Promise.resolve(title),
        session.title
      )
      return {...session, title: mTitle}
    })
  )

export async function getAssignmentTitleForElaProduct(
  t: TranslateT,
  session: GenericAssignmentSessionAttrsT,
  skills: {uspId: string; shortName: string}[]
): Promise<HTMLText | PlainText | undefined | null> {
  switch (session.metadata.tag) {
    case 'math_adaptive':
    case 'math_targeted':
    case 'focus_skills_practice':
    case 'math_assessments':
    case 'math_fact_practice':
    case 'math_ibls':
    case 'math_number_facts':
    case 'math_number_basics':
    case 'math_depth_of_knowledge_practice':
    case 'math_targeted_depth_of_knowledge_practice':
      return null
    case 'ela_decodables': {
      const targetDecodableUuid = session.metadata.contents.decodableUuid
      // TODO: fetch with react query
      const decodableInfoCollection = await fetchDecodableInfos()

      const maybeDecodable = decodableInfoCollection.find(
        decodableInfo => decodableInfo.uuid === targetDecodableUuid
      )

      return mmap(
        targetDecodable => getDecodableTitle(targetDecodable.title, session.title),
        maybeDecodable
      )
    }
    case 'ela_targeted_skills_practice': {
      const standard = session.metadata.contents.standard
      return getTargetedSkillsTitle(standard, t, session.title)
    }
    case 'ela_focus_skills_practice': {
      return getFocusSkillsTitle(t, session.title)
    }
    case 'ela_adaptive_skills_practice': {
      const {domain, rlStandard} = session.metadata.contents
      return getAdaptiveSkillsTitle(domain, t, session.title, rlStandard)
    }
    case 'ela_word_study': {
      const {rlStandard, rlSkill} = session.metadata.contents
      const mSkill = mthen(rlSkill, ({uspId}) => skills.find(s => s.uspId === uspId))
      return getWordStudyTitle(rlStandard, mSkill, t, session.title)
    }
    case 'ela_sight_words':
      return null
    case 'ela_articles_reading': {
      const articleUuid = session.metadata.contents.articleUuid
      const elaArticleInfoCollection = await fetchElaArticleInfoCollection()

      return getTitleForArticle(articleUuid, elaArticleInfoCollection, session.title)
    }

    case 'ela_articles_writing': {
      const uuid = session.metadata.contents.articleUuid

      const articleInfoCollection = await fetchElaArticleInfoCollection()

      return getTitleForArticle(uuid, articleInfoCollection, session.title)
    }
    case 'ela_assessments': {
      const assessmentContentUuid = session.metadata.contents.assessmentContentUuid
      // TODO: fetch with react query
      const elaAssessments = await fetchElaAssessments()

      const mLetter = getElaAssessmentLetter(elaAssessments, assessmentContentUuid)

      return mmap(letter => getAssessmentTitle(letter, t, session.title), mLetter)
    }
    case 'science_articles_reading':
    case 'science_articles_writing':
    case 'social_studies_articles_reading':
    case 'social_studies_articles_writing':
      return null
    default:
      return exhaustive(session.metadata, 'ProductT')
  }
}

function getTitleForArticle(
  articleUuid: string,
  elaArticleInfoCollection: Array<ElaArticleInfoAttrs>,
  assignmentTitle?: HTMLText | PlainText | null
): HTMLText | PlainText | undefined | null {
  const mArticleInfo = elaArticleInfoCollection.find(
    articleInfo => articleInfo.uuid === articleUuid
  )

  return mmap(articleInfo => getArticleTitle(articleInfo.title, assignmentTitle), mArticleInfo)
}
